/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import { SectionConfigItem } from './entries';
import { BasicUserRoles } from './enums';

import type { AppRouteItem } from '../../../stores/router';
import type { ConfiguratorServiceConfig, UserConfig, UserConfigData } from './interfaces';

export class ConfiguratorService<Roles extends string> {

    protected allRolesPaths: ConfiguratorServiceConfig<Roles>['allRolesPaths'];
    protected loadedUserRole: keyof Roles;
    protected routes: AppRouteItem[] = [];
    protected sections: Record<string, SectionConfigItem<any>> = {};

    constructor(configuratorServiceConfig: ConfiguratorServiceConfig<Roles>) {
        const {
            allRolesPaths,
        } = configuratorServiceConfig;

        this.allRolesPaths = allRolesPaths;
    }

    isUserRoleAllowed(userRole: keyof typeof this.allRolesPaths): boolean {
        return Object.keys(this.allRolesPaths).includes(userRole) || Boolean(this.allRolesPaths[BasicUserRoles.All]);
    }

    getUserRoutes(userRole: keyof typeof this.allRolesPaths): Promise<AppRouteItem[]> {
        if (!this.isUserRoleAllowed(userRole)) {
            console.error(`User role "${userRole}" not allowed to that portal`);
            return Promise.resolve([]);
        }

        if (this.loadedUserRole === userRole) {
            return Promise.resolve(this.routes);
        }

        return this.loadConfigFor(userRole)
            .then(() => this.routes);
    }

    getSectionConfig<T>(sectionId: string): SectionConfigItem<T> | null {
        return this.sections[sectionId] || null;
    }

    protected loadConfigFor(userRole: keyof typeof this.allRolesPaths): Promise<void> {
        return this.allRolesPaths[userRole]()
            .then((config) => {
                this.setConfig(this.prepareUserConfig(config as UserConfigData<keyof Roles, any>));
            })
            .catch((err) => {
                err && console.error(err);
                this.setConfig({ role: userRole as keyof Roles, routes: [], sections: [] });
            });
    }

    protected setConfig({ role, routes, sections }: UserConfig<keyof Roles, any>): void {
        this.loadedUserRole = role;
        this.routes = routes;
        this.sections = Object.fromEntries(sections.map(section => [section.id, section]));
    }

    protected prepareUserConfig(config: UserConfigData<keyof Roles, any>): UserConfig<keyof Roles, any> {
        const { sections, ...restConfig } = config;

        return {
            ...restConfig,
            sections: sections.map(section => new SectionConfigItem<any>(section)),
        };
    }

}
